import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter, map } from 'rxjs';

import {
  EntityCfg,
  EntityInterface,
  makeTableColumns
} from '@gfe/core/models/entities/entity-visualization-interface';
import { EntityLabelsTranslationService } from '@gfe/core/services/entity-labels-translation/entity-labels-translation.service';
import {
  ReferenceNamesService,
  ReferenceResolutionContext
} from '@gfe/core/services/reference-names/reference-names.service';
import { SelectorsService } from '@gfe/core/services/selectors/selectors.service';
import { SplitColumnsService } from '@gfe/core/services/split-columns/split-columns.service';
import { GenericEntityRegistryService } from '@gfe/core/services/user-models-registry/generic-entity-registry.service';
import { selectDataspaceState } from '@gfe/core/store/dataspace/dataspace.selectors';
import { selectPlan } from '@gfe/core/store/plan/plan.selectors';
import { selectTables } from '@gfe/core/store/user-preferences/user-preferences.selectors';
import { isNil } from '@gfe/core/utils/shared-utils';
import { ReferenceNameLookup } from '@gfe/shared/components/table/table-content/table-content.component';
import { TableColumn, TableConfig } from '@gfe/shared/components/table/table.model';

import { TableComponent } from '@shared/components/table/table.component';

import {
  NameModeReferenceColumn,
  collectReferenceUidsByType,
  selectNameModeReferenceColumns
} from '../entities/entity/entity-reference-names.util';

export interface EntityDetailInput {
  entity: EntityInterface<unknown>;
  detailsEntityType: string;
}

@Component({
  selector: 'app-entity-details',
  imports: [TableComponent],
  templateUrl: './entity-details.component.html',
  styles: ``
})
export class EntityDetailsComponent {
  private readonly translate = inject(TranslateService);
  private readonly entityLabelsTranslationService = inject(EntityLabelsTranslationService);
  private readonly splitColumnsService = inject(SplitColumnsService);
  private readonly selectorsService = inject(SelectorsService);
  private readonly genericEntityRegistry = inject(GenericEntityRegistryService);
  private readonly store = inject(Store);
  private readonly referenceNamesService = inject(ReferenceNamesService);

  private readonly dataspace = this.store.selectSignal(selectDataspaceState);
  private readonly plan = this.store.selectSignal(selectPlan);
  private readonly storedTables = this.store.selectSignal(selectTables);

  entityInput = input.required<EntityDetailInput>();

  protected entity = computed(() => this.entityInput().entity);
  protected entityType = computed<string>(() => this.entityInput().detailsEntityType);

  private entityCfg = computed<EntityCfg<unknown> | undefined>(() => {
    return this.entity().getDetailsCfg(this.entityType());
  });

  protected tableId = computed<string>(() => 'details-' + this.entityType());

  /** entityType -> (uid -> name), accumulated as reference names resolve. */
  protected readonly resolvedReferenceNames = signal<Map<string, Map<string, string>>>(
    new Map()
  );

  /** Lookup bound to the table's `referenceNames` input. */
  protected readonly referenceNameLookup: ReferenceNameLookup = (entityType, uid) =>
    this.resolvedReferenceNames().get(entityType)?.get(uid);

  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  protected tableEntities = computed<EntityInterface<unknown>[]>(() =>
    this.entity().getDetails(this.entityType())
  );

  protected tableConfig: TableConfig<EntityInterface<unknown>> = {
    rowId: 'uid' as keyof EntityInterface<unknown>,
    bordered: true,
    horizontalScroll: true,
    columnActionSettings: {
      sortable: false,
      filterable: false,
      resizeable: true,
      groupable: false,
      movable: true,
      hideable: true
    }
  };

  constructor() {
    // The detail panel builds columns from a cfg instantiated outside the registry
    // (route.getDetailsCfg), so it must load the split-column target entities itself.
    // Fetching under the detail entity's own versionName keeps the cache key aligned
    // with the getter used in `columns`. Detail panels are read-only → 'visualization'.
    effect(() => {
      const tableCfg = this.entityCfg();
      if (isNil(tableCfg)) {
        return;
      }
      this.splitColumnsService.load(
        tableCfg.versionName(),
        'visualization',
        this.selectorsService
      );

      // Some detail cfgs (e.g. route_action) declare their split targets on the FE registry
      // instead of the wire schema, so load those explicitly too.
      const targets = this.genericEntityRegistry.getSplitColumnTargets(
        tableCfg.versionName()
      );
      if (targets.length > 0) {
        this.splitColumnsService.loadTargets(
          tableCfg.versionName(),
          targets,
          'visualization',
          this.selectorsService
        );
      }
    });

    // Resolve reference names for the detail table's name-mode reference columns.
    // Mirrors EntityComponent's resolving effect; keyed by this table's own
    // `tableId` so its display-mode toggles are independent from the main table.
    // Note: this effect must never read `resolvedReferenceNames` (the signal it
    // writes to via .update()), otherwise it would re-trigger itself in a loop.
    effect(() => {
      const ctx = this.referenceContext();
      const cols = this.nameModeReferenceColumns();
      const rows = this.tableEntities();
      if (isNil(ctx) || cols.length === 0 || rows.length === 0) return;

      const uidsByType = collectReferenceUidsByType(cols, rows);

      for (const [entityType, uids] of uidsByType) {
        this.referenceNamesService
          .resolveNames$(entityType, uids, ctx)
          .subscribe((resolved) => {
            this.resolvedReferenceNames.update((prev) => {
              const next = new Map(prev);
              const merged = new Map(next.get(entityType) ?? new Map<string, string>());
              for (const [uid, name] of resolved) merged.set(uid, name);
              next.set(entityType, merged);
              return next;
            });
          });
      }
    });
  }

  /** Resolution context (dataspace + plan) for reference-name lookups, or null if unknown. */
  private referenceContext(): ReferenceResolutionContext | null {
    const dataSpaceId = this.dataspace().dataspace?.id;
    if (isNil(dataSpaceId)) return null;
    const planId = this.plan()?.id;
    return isNil(planId) ? { dataSpaceId } : { dataSpaceId, planId };
  }

  /**
   * Reference columns currently set to name mode, with their target entity type.
   *
   * The stored per-column config is keyed by this table's `tableId`
   * (`details-<entityType>`), so toggling display mode here is tracked
   * independently from the main entity table.
   */
  private nameModeReferenceColumns(): NameModeReferenceColumn<EntityInterface<unknown>>[] {
    return selectNameModeReferenceColumns(
      this.columns(),
      this.storedTables()?.[this.tableId()]
    );
  }

  protected columns = computed(() => {
    this.currentLang();
    // Rebuild once split-column target entities (e.g. capacity resources) arrive.
    this.splitColumnsService.splitEntities();

    const tableCfg = this.entityCfg();
    if (isNil(tableCfg)) {
      return [];
    }

    const versionName = tableCfg.versionName();
    const rawColumns = makeTableColumns(tableCfg, (targetVersionName) =>
      this.splitColumnsService.entitiesForVersion(versionName, targetVersionName)
    );
    return this.entityLabelsTranslationService
      .translateTableColumns(rawColumns)
      .map((column: TableColumn<EntityInterface<unknown>>) => {
        return column;
      });
  });
}
